/**
 * Word count utilities for editor content
 */

import { Value } from '@udecode/plate';

/**
 * Extract plain text from Plate editor content
 * @param content - The Plate editor content
 * @returns Plain text string
 */
export const extractTextFromPlateContent = (content: Value): string => {
  if (!content || !Array.isArray(content)) {
    return '';
  }

  return content.map(block => {
    if (block.children) {
      return block.children.map((child: any) => child.text || '').join('');
    }
    return '';
  }).join(' ');
};

/**
 * Count words in a text string
 * @param text - The text to count words in
 * @returns Number of words
 */
export const countWords = (text: string): number => {
  if (!text || typeof text !== 'string') {
    return 0;
  }

  // Remove extra whitespace and split by whitespace
  const words = text.trim().split(/\s+/);
  
  // Filter out empty strings
  const filteredWords = words.filter(word => word.length > 0);
  
  return filteredWords.length;
};

/**
 * Count words in Plate editor content
 * @param content - The Plate editor content
 * @returns Number of words
 */
export const countWordsInPlateContent = (content: Value): number => {
  const text = extractTextFromPlateContent(content);
  return countWords(text);
};

/**
 * Count characters in Plate editor content (excluding spaces)
 * @param content - The Plate editor content
 * @returns Number of characters
 */
export const countCharactersInPlateContent = (content: Value): number => {
  const text = extractTextFromPlateContent(content);
  return text.replace(/\s/g, '').length;
};

/**
 * Count characters in Plate editor content (including spaces)
 * @param content - The Plate editor content
 * @returns Number of characters including spaces
 */
export const countCharactersWithSpacesInPlateContent = (content: Value): number => {
  const text = extractTextFromPlateContent(content);
  return text.length;
};

/**
 * Get reading time estimate in minutes
 * @param wordCount - Number of words
 * @param wordsPerMinute - Reading speed (default: 200 words per minute)
 * @returns Reading time in minutes
 */
export const getReadingTime = (wordCount: number, wordsPerMinute: number = 200): number => {
  if (wordCount <= 0) return 0;
  return Math.ceil(wordCount / wordsPerMinute);
};

/**
 * Format reading time as a human-readable string
 * @param minutes - Reading time in minutes
 * @returns Formatted reading time string
 */
export const formatReadingTime = (minutes: number): string => {
  if (minutes === 0) return '0 min read';
  if (minutes === 1) return '1 min read';
  if (minutes < 60) return `${minutes} min read`;
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  if (remainingMinutes === 0) {
    return hours === 1 ? '1 hr read' : `${hours} hrs read`;
  }
  
  return `${hours}h ${remainingMinutes}m read`;
};
